import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { toast } from '@/hooks/use-toast';

export interface CartAddon {
  id: string;
  name: string;
  price: number;
  quantity: number;
}

export interface CartItem {
  id: string;
  name: string;
  price: number;
  originalPrice?: number; // Preço original sem promoção (para referência)
  image?: string;
  quantity: number;
  restaurantId: string;
  restaurantName: string;
  notes?: string;
  addons?: CartAddon[];
}

interface CartContextType {
  items: CartItem[];
  addItem: (item: Omit<CartItem, 'quantity'> & { quantity: number }) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  getTotalItems: () => number;
  getTotalPrice: () => number;
  getRestaurantId: () => string | null;
  isCartOpen: boolean;
  setIsCartOpen: (open: boolean) => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('sg-sabores-cart');
    if (savedCart) {
      try {
        setItems(JSON.parse(savedCart));
      } catch (error) {
        console.error('Error loading cart from localStorage:', error);
      }
    }
  }, []);

  // Save cart to localStorage whenever items change
  useEffect(() => {
    localStorage.setItem('sg-sabores-cart', JSON.stringify(items));
  }, [items]);

  const addItem = (newItem: Omit<CartItem, 'quantity'> & { quantity: number }) => {
    setItems(currentItems => {
      // Create unique identifier based on item + addons combination
      const createItemKey = (item: typeof newItem) => {
        const addonsKey = item.addons
          ?.map(addon => `${addon.id}:${addon.quantity}`)
          .sort()
          .join('|') || '';
        return `${item.id}-${addonsKey}`;
      };

      const newItemKey = createItemKey(newItem);
      
      // Check if cart has items from different restaurant
      if (currentItems.length > 0 && currentItems[0].restaurantId !== newItem.restaurantId) {
        toast({
          variant: "destructive",
          title: "Carrinho limpo",
          description: "Você só pode pedir de um restaurante por vez. O carrinho anterior foi limpo.",
        });
        // Create unique ID for new item
        const uniqueId = `${newItem.id}-${Date.now()}`;
        return [{ ...newItem, id: uniqueId, quantity: newItem.quantity }];
      }

      // Check if exact same item with same addons already exists
      const existingItemIndex = currentItems.findIndex(item => {
        const existingItemKey = createItemKey(item);
        return existingItemKey === newItemKey;
      });
      
      if (existingItemIndex >= 0) {
        // Update quantity of existing identical item
        return currentItems.map((item, index) => {
          if (index === existingItemIndex) {
            return { 
              ...item, 
              quantity: item.quantity + newItem.quantity
            };
          }
          return item;
        });
      }

      toast({
        title: "Item adicionado!",
        description: `${newItem.name} foi adicionado ao carrinho.`,
      });

      // Add as new item with unique ID
      const uniqueId = `${newItem.id}-${Date.now()}`;
      return [...currentItems, { ...newItem, id: uniqueId, quantity: newItem.quantity }];
    });
  };

  const removeItem = (id: string) => {
    setItems(currentItems => {
      const item = currentItems.find(item => item.id === id);
      if (item) {
        toast({
          title: "Item removido",
          description: `${item.name} foi removido do carrinho.`,
        });
      }
      return currentItems.filter(item => item.id !== id);
    });
  };

  const updateQuantity = (id: string, quantity: number) => {
    if (quantity <= 0) {
      removeItem(id);
      return;
    }

    setItems(currentItems =>
      currentItems.map(item => {
        if (item.id === id) {
          const quantityRatio = quantity / item.quantity;
          
          // Update addon quantities proportionally
          const updatedAddons = item.addons?.map(addon => ({
            ...addon,
            quantity: Math.round(addon.quantity * quantityRatio)
          }));

          return { 
            ...item, 
            quantity,
            addons: updatedAddons
          };
        }
        return item;
      })
    );
  };

  const clearCart = () => {
    setItems([]);
    toast({
      title: "Carrinho limpo",
      description: "Todos os itens foram removidos.",
    });
  };

  const getTotalItems = () => {
    return items.reduce((total, item) => total + item.quantity, 0);
  };

  const getTotalPrice = () => {
    return items.reduce((total, item) => {
      const itemTotal = item.price * item.quantity;
      const addonsTotal = item.addons?.reduce((sum, addon) => sum + (addon.price * addon.quantity * item.quantity), 0) || 0;
      return total + itemTotal + addonsTotal;
    }, 0);
  };

  const getRestaurantId = () => {
    return items.length > 0 ? items[0].restaurantId : null;
  };

  return (
    <CartContext.Provider value={{
      items,
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
      getTotalItems,
      getTotalPrice,
      getRestaurantId,
      isCartOpen,
      setIsCartOpen,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    // During app initialization, provide safe defaults instead of throwing
    if (typeof window !== 'undefined' && window.location) {
      console.warn('useCart called outside CartProvider context - providing safe defaults');
      return {
        items: [],
        addItem: () => {},
        removeItem: () => {},
        updateQuantity: () => {},
        clearCart: () => {},
        getTotalItems: () => 0,
        getTotalPrice: () => 0,
        getRestaurantId: () => null,
        isCartOpen: false,
        setIsCartOpen: () => {},
      };
    }
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}